import { z } from 'zod';

/**
 * Verification before a truck returns to service (gitaumoses4/chukua#171). Every attempt is a row on
 * `work_order_verifications`: a checklist, the photos, and an outcome that is derived from the checklist, never typed.
 */

/** The fixed list a verifier answers; Zod holds it (not a CHECK), so a new item is a code change. */
export const VERIFICATION_CHECKLIST_ITEMS = ['work_matches_order', 'defect_cleared', 'no_new_damage', 'road_ready'] as const;
export type VerificationChecklistItem = (typeof VERIFICATION_CHECKLIST_ITEMS)[number];

export const VERIFICATION_RESULTS = ['pass', 'fail'] as const;
export type VerificationResult = (typeof VERIFICATION_RESULTS)[number];

/** Hard-coded in a CHECK on `work_order_verifications`. */
export const VERIFICATION_OUTCOMES = ['passed', 'failed'] as const;
export type VerificationOutcome = (typeof VERIFICATION_OUTCOMES)[number];

/** The entity type a verification photo is uploaded under, with the work order's id as the entity id. */
export const WORK_ORDER_VERIFICATION_UPLOAD_ENTITY_TYPE = 'work_order_verification';

const text = (max: number) => z.string().trim().min(1).max(max);

const checklistEntrySchema = z
  .object({ item: z.enum(VERIFICATION_CHECKLIST_ITEMS), result: z.enum(VERIFICATION_RESULTS), note: text(500).optional() })
  .strict();
export type VerificationChecklistEntry = z.infer<typeof checklistEntrySchema>;

/** `POST /work-orders/:id/verifications` — the outcome follows from the checklist: all pass closes the order, any fail reopens it. */
export const recordVerificationInputSchema = z
  .object({
    checklist: z.array(checklistEntrySchema),
    /** Why the work failed; required when any item fails. */
    reason: text(500).optional(),
    photoUploadIds: z.array(z.uuid()).max(10).default([]),
  })
  .strict()
  .refine(
    (input) =>
      new Set(input.checklist.map((entry) => entry.item)).size === VERIFICATION_CHECKLIST_ITEMS.length &&
      input.checklist.length === VERIFICATION_CHECKLIST_ITEMS.length,
    {
      message: `The checklist answers each of ${VERIFICATION_CHECKLIST_ITEMS.join(', ')} exactly once`,
      path: ['checklist'],
    },
  )
  .refine((input) => new Set(input.photoUploadIds).size === input.photoUploadIds.length, { message: 'A photo is listed once', path: ['photoUploadIds'] });
export type RecordVerificationInput = z.infer<typeof recordVerificationInputSchema>;

export interface WorkOrderVerificationView {
  id: string;
  workOrderId: string;
  verifierId: string;
  outcome: VerificationOutcome;
  checklist: VerificationChecklistEntry[];
  photoUploadIds: string[];
  reason: string | null;
  createdAt: string;
}
